import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { API } from '../utils/api';
import { getTrack } from '../config/tracksCatalog';
import LucideIcon from './LucideIcon';

const panel: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 22,
};

/**
 * Lesson list for one Frameworks/Production track -- the real destination
 * both catalogs' cards link into. Renders the same completed/unlocked/locked
 * sequencing Challenges.tsx already uses for the Tier-1 track, then hands off
 * to the existing generic /lessons/:lessonId route (Lesson.tsx) for the
 * actual lesson -- this screen is purely discovery/navigation, not a second
 * lesson renderer.
 */
const TrackLessonList: React.FC = () => {
  const { trackSlug } = useParams();
  const navigate = useNavigate();
  const track = getTrack(trackSlug);
  const [completedIds, setCompletedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!track) return;
    let cancelled = false;
    API.get('/progress')
      .then((response: any) => {
        if (cancelled) return;
        const progress = response?.[0]?.topicProgress?.[track.slug];
        setCompletedIds(progress?.completedChallenges || []);
      })
      .catch(() => { /* fail open: nothing completed yet, everything but lesson 1 stays locked */ })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [track]);

  if (!track) {
    return (
      <div style={{ ...panel, maxWidth: 480, margin: '0 auto', textAlign: 'center' }}>
        <p style={{ margin: '0 0 14px', fontSize: 14, color: 'var(--text-secondary)' }}>That track doesn&rsquo;t exist.</p>
        <Link to="/frameworks" className="btn-live" style={{ display: 'inline-block', background: 'var(--ink)', color: 'white', fontWeight: 700, fontSize: 14, padding: '10px 20px', borderRadius: 10, textDecoration: 'none' }}>
          Back to Frameworks
        </Link>
      </div>
    );
  }

  const backTo = track.category === 'production' ? '/production' : '/frameworks';
  const backLabel = track.category === 'production' ? 'Production & Observability' : 'Frameworks Catalog';

  // Find the first unlocked but incomplete lesson to guide the learner directly
  const nextLessonIndex = track.lessons.findIndex((l) => !completedIds.includes(l.id));
  const activeLesson = nextLessonIndex !== -1 ? track.lessons[nextLessonIndex] : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 840 }}>
      {/* Top Navigation & Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <Link
          to={backTo}
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--text-secondary)',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          &larr; Back to {backLabel}
        </Link>
        {track.trackNumber && (
          <span
            className="ws-mono"
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.08em',
              padding: '3px 8px',
              borderRadius: 6,
              background: 'var(--surface-sunken)',
              border: '1px solid var(--border)',
              color: 'var(--ember-strong)',
            }}
          >
            TRACK {String(track.trackNumber).padStart(2, '0')}
          </span>
        )}
      </div>

      {/* Track Overview & Syllabus Introduction */}
      <div className="ws-fade-up" style={{ ...panel, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              {track.difficulty && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: 'var(--ember-strong)',
                    background: 'var(--ember-soft)',
                    padding: '2px 8px',
                    borderRadius: 999,
                  }}
                >
                  {track.difficulty}
                </span>
              )}
              {track.estimatedTime && (
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                  &middot; ~{track.estimatedTime} total
                </span>
              )}
              <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                &middot; {track.lessons.length} Step-by-Step Lessons
              </span>
            </div>

            <h1 className="ws-display" style={{ margin: '0 0 8px', fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }}>
              {track.name}
            </h1>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.55, maxWidth: 640 }}>
              {track.description}
            </p>
          </div>

          {/* Quick Start Next Lesson Button */}
          {activeLesson && (
            <button
              type="button"
              onClick={() => navigate(`/lessons/${activeLesson.id}`)}
              className="btn-live ws-display"
              style={{
                background: 'var(--ember)',
                color: 'white',
                border: 'none',
                borderRadius: 10,
                padding: '12px 20px',
                fontSize: 13.5,
                fontWeight: 700,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              <span>{completedIds.length === 0 ? 'Start Step 01' : `Resume Step ${String(nextLessonIndex + 1).padStart(2, '0')}`}</span>
              <span>&rarr;</span>
            </button>
          )}
        </div>

        {/* Why this track matters */}
        {track.whyItMatters && (
          <div style={{ background: 'var(--surface-sunken)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px' }}>
            <span className="ws-mono" style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', letterSpacing: '0.05em', display: 'block', marginBottom: 4 }}>
              Production Engineering Context
            </span>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5 }}>
              {track.whyItMatters}
            </p>
          </div>
        )}

        {/* What you will learn in this track */}
        {track.outcomes && track.outcomes.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span className="ws-mono" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)', letterSpacing: '0.05em' }}>
              What You Will Master in This Track
            </span>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
              {track.outcomes.map((outcome, idx) => (
                <div
                  key={idx}
                  style={{
                    background: 'var(--surface-sunken)',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    padding: '10px 12px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                  }}
                >
                  <span style={{ color: 'var(--ember)', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>✓</span>
                  <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.45 }}>{outcome}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Track Completion Progress */}
        <div style={{ paddingTop: 4 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 6 }}>
            <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>
              Curriculum Progress: {completedIds.length} of {track.lessons.length} Steps Completed
            </span>
            <span>{Math.round((completedIds.length / track.lessons.length) * 100)}%</span>
          </div>
          <div style={{ height: 8, borderRadius: 999, background: 'var(--surface-sunken)', border: '1px solid var(--border)', overflow: 'hidden' }}>
            <div
              style={{
                width: `${(completedIds.length / track.lessons.length) * 100}%`,
                height: '100%',
                borderRadius: 999,
                background: completedIds.length === track.lessons.length ? 'var(--success)' : 'var(--ember)',
                transition: 'width 0.4s ease',
              }}
            />
          </div>
        </div>
      </div>

      {/* Guided Lesson Stepper with Clear Numbers and Direction */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 4px' }}>
          <h2 className="ws-display" style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
            Sequential Learning Roadmap
          </h2>
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
            Step through lessons in numerical order
          </span>
        </div>

        {track.lessons.map((lesson, index) => {
          const isComplete = completedIds.includes(lesson.id);
          const isUnlocked = index === 0 || completedIds.includes(track.lessons[index - 1].id) || isComplete;
          const isNextRecommended = !isComplete && isUnlocked && (index === 0 || completedIds.includes(track.lessons[index - 1].id));

          return (
            <div
              key={lesson.id}
              onClick={() => isUnlocked && navigate(`/lessons/${lesson.id}`)}
              role={isUnlocked ? 'button' : undefined}
              tabIndex={isUnlocked ? 0 : undefined}
              onKeyDown={(e) => {
                if (isUnlocked && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  navigate(`/lessons/${lesson.id}`);
                }
              }}
              style={{
                ...panel,
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                cursor: isUnlocked ? 'pointer' : 'not-allowed',
                opacity: isUnlocked ? 1 : 0.55,
                border: isNextRecommended
                  ? '1.5px solid var(--ember)'
                  : isComplete
                  ? '1px solid oklch(0.65 0.15 155 / 0.4)'
                  : '1px solid var(--border)',
                background: isNextRecommended
                  ? 'oklch(0.68 0.18 48 / 0.04)'
                  : 'var(--surface)',
                position: 'relative',
                transition: 'border-color 0.2s ease, transform 0.15s ease',
              }}
            >
              {/* Step Number Circle */}
              <div
                style={{
                  flex: '0 0 36px',
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12.5,
                  fontWeight: 700,
                  background: isComplete
                    ? 'var(--success)'
                    : isNextRecommended
                    ? 'var(--ember)'
                    : isUnlocked
                    ? 'var(--surface-sunken)'
                    : 'var(--surface-sunken)',
                  color: isComplete || isNextRecommended ? 'white' : 'var(--text-tertiary)',
                  border: isNextRecommended ? 'none' : '1px solid var(--border)',
                }}
              >
                {isComplete ? (
                  <LucideIcon name="status-check" size={15} />
                ) : (
                  String(index + 1).padStart(2, '0')
                )}
              </div>

              {/* Lesson Details */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span
                    className="ws-mono"
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: isNextRecommended ? 'var(--ember-strong)' : 'var(--text-tertiary)',
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                    }}
                  >
                    STEP {String(index + 1).padStart(2, '0')}
                  </span>
                  {isNextRecommended && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: '1px 7px',
                        borderRadius: 999,
                        background: 'var(--ember-soft)',
                        color: 'var(--ember-strong)',
                        border: '1px solid var(--ember)',
                      }}
                    >
                      RECOMMENDED NEXT
                    </span>
                  )}
                  {isComplete && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: '1px 7px',
                        borderRadius: 999,
                        background: 'oklch(0.65 0.15 155 / 0.15)',
                        color: 'var(--success)',
                      }}
                    >
                      PASSED &middot; 100 PTS
                    </span>
                  )}
                </div>

                <span
                  style={{
                    fontSize: 14.5,
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    lineHeight: 1.4,
                  }}
                >
                  {lesson.title}
                </span>

                <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                  Interactive Study &middot; Scaffolding &middot; Graded Implementation
                </span>
              </div>

              {/* Action Affordance */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                {isNextRecommended ? (
                  <span
                    className="btn-live ws-display"
                    style={{
                      fontSize: 12.5,
                      fontWeight: 700,
                      background: 'var(--ember)',
                      color: 'white',
                      padding: '7px 14px',
                      borderRadius: 8,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    Start Step &rarr;
                  </span>
                ) : isComplete ? (
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)' }}>
                    Review &rarr;
                  </span>
                ) : isUnlocked ? (
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ember)' }}>
                    Open &rarr;
                  </span>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-tertiary)' }}>
                    <LucideIcon name="status-locked" size={13} />
                    <span className="ws-mono" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                      Complete #{index} first
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TrackLessonList;
